<script>
import { iFilter } from '@inscribe/signal';
import { getAndSyncQueryParams, updateQueryParams } from '@/utils/queryParams';
import { InscribeEventsLogger, INSCRIBE_EVENTS } from '@/events-logger';
import axios from 'axios';

export default {
  components: {
    iFilter,
  },
  props: {
    analyticsLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      params: getAndSyncQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      }),
      options: [],
      selectedCustomerOptions: [],
      optionsAreLoading: false,
    };
  },
  computed: {
    value() {
      return this.selectedCustomerOptions.map((option) => option.label);
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
        this.loadCustomerOptions();
      },
    },
  },
  async mounted() {
    await this.loadCustomerOptions();
  },
  methods: {
    async loadCustomerOptions() {
      const [selectedCustomerOptions, searchedCustomersOptions] =
        await Promise.all([
          this.getSelectedCustomerOptions(),
          this.getSearchedCustomerOptions(this.searchString),
        ]);

      this.selectedCustomerOptions = selectedCustomerOptions;
      this.options = this.combineSelectedAndSearchedOptions(
        searchedCustomersOptions,
      );
    },
    async getSelectedCustomerOptions() {
      const commaSeparatedSelectedCustomerIds = decodeURIComponent(
        this.params.customer_ids || '',
      );
      const selectedCustomers = await this.getCustomersBySelectedUUIDs(
        commaSeparatedSelectedCustomerIds,
      );

      return this.getOptionsFromCustomers(selectedCustomers);
    },
    async getSearchedCustomerOptions(searchTerm) {
      const searchedCustomers = await this.getCustomersBySearch(searchTerm);
      return this.getOptionsFromCustomers(searchedCustomers);
    },
    combineSelectedAndSearchedOptions(searchedCustomerOptions) {
      // Filter out selected options from the searched options to avoid duplicates
      const searchedCustomerOptionsFiltered = searchedCustomerOptions.filter(
        (option) =>
          !this.selectedCustomerOptions.some((t) => t.value === option.value),
      );

      return [
        ...this.selectedCustomerOptions,
        ...searchedCustomerOptionsFiltered,
      ];
    },
    getOptionsFromCustomers(customers) {
      if (customers.length > 0) {
        return customers.map((customer) => ({
          label: customer.name,
          value: customer.id,
        }));
      }
      return [];
    },
    async getCustomersBySelectedUUIDs(commaSeparatedCustomerUUIDs) {
      if (commaSeparatedCustomerUUIDs === '') {
        return [];
      }

      if (this.getCustomersBySelectedUUIDsController) {
        this.getCustomersBySelectedUUIDsController.abort();
      }
      this.getCustomersBySelectedUUIDsController = new AbortController();

      const queryParams = {
        customer_ids: commaSeparatedCustomerUUIDs,
      };
      try {
        const response = await axios.get('/api/v2/customers/', {
          params: queryParams,
          signal: this.getCustomersBySelectedUUIDsController.signal,
        });
        return response.data.data;
      } catch (error) {
        return [];
      }
    },
    async getCustomersBySearch(value) {
      if (this.getCustomersBySearchController) {
        this.getCustomersBySearchController.abort();
      }
      this.getCustomersBySearchController = new AbortController();

      const queryParams = {
        search: value,
        limit: 10,
        page: 1,
        style: 'advanced',
      };

      try {
        this.optionsAreLoading = true;
        const response = await axios.get('/api/v2/customers/', {
          params: queryParams,
          signal: this.getCustomersBySearchController.signal,
        });
        this.optionsAreLoading = false;
        return response.data.data;
      } catch (error) {
        this.optionsAreLoading = false;
        return [];
      }
    },
    async onSearchTextChanged(searchTerm) {
      const searchedOptions = await this.getSearchedCustomerOptions(searchTerm);
      this.options = this.combineSelectedAndSearchedOptions(searchedOptions);
    },
    onSelectionChanged() {
      const commaSeparatedCustomerIds = this.selectedCustomerOptions
        .map((option) => option.value)
        .join(',');

      updateQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
        updates: {
          ['customer_ids']:
            commaSeparatedCustomerIds.length > 0
              ? commaSeparatedCustomerIds
              : undefined,
        },
      });
      InscribeEventsLogger.logEvent(INSCRIBE_EVENTS.CUSTOMERS_FILTER_CLICKED, {
        itemName: 'filter_customers',
      });
    },
    onOptionSelectionChanged(selectedCustomerOptions) {
      this.selectedCustomerOptions = [...selectedCustomerOptions.selection];
    },
  },
};
</script>

<template>
  <iFilter
    label="Customer"
    data-testid="customer-ids-filter"
    :data-dd-action-name="analyticsLabel"
    :debounce-time="400"
    :options="options"
    :model-value="value"
    :searchable="true"
    :show-selected-list="true"
    :show-empty-create-button="false"
    :options-are-loading="optionsAreLoading"
    :search-debounce-time="300"
    :filter-items-by-search-term="false"
    @selection-changed="onSelectionChanged"
    @search-text-changed="onSearchTextChanged"
    @option-selection-toggled="onOptionSelectionChanged"
  />
</template>
